import { __assign, __awaiter, __extends, __generator } from "tslib";
import { StratusClient } from '../stratus-client';
/**
 * Client for making UI Management Service API calls.
 */
var UiMgtSvcClient = /** @class */ (function (_super) {
    __extends(UiMgtSvcClient, _super);
    function UiMgtSvcClient() {
        var _this = _super !== null && _super.apply(this, arguments) || this;
        _this.apiName = 'ws-hp.com/uimgtsvc';
        _this.apiVersion = '2';
        _this.uiInfoEndpoint = '/uiinfos';
        _this.consentsEndpoint = '/consents';
        _this.countriesInfoEndpoint = '/systeminfos';
        _this.programLocalesEndpoint = '/programlocales';
        return _this;
    }
    /**
     * Retrieves the list of program locales for a resource
     *
     * @param {Array<string>} programIdList - array of program IDs
     * @param headers - custom headers object
     * @returns Axios response object
     */
    UiMgtSvcClient.prototype.getProgramLocales = function (programIdList, headers) {
        if (headers === void 0) { headers = {}; }
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                return [2 /*return*/, this.jarvisWebHttpInstance.get({
                        url: this.programLocalesEndpoint,
                        params: {
                            programIdList: programIdList.join(','),
                        },
                        headers: __assign({ Accept: 'application/json' }, headers),
                    })];
            });
        });
    };
    /**
     * Returns the expected schema for the UI Info response object.
     *
     * @returns schema in object format
     */
    UiMgtSvcClient.uiInfoSchema = function () {
        return {
            $schema: 'http://json-schema.org/schema#',
            type: 'object',
            required: ['uiInfo'],
            additionalProperties: true,
            properties: {
                uiInfo: {
                    type: ['object', 'null'],
                    required: ['key'],
                    additionalProperties: true,
                    properties: {
                        key: {
                            type: 'string',
                        },
                    },
                },
            },
        };
    };
    /**
     * Retrieves the next UIInfo given an optional current UIInfo and ExtensionInfo.
     *
     * @param {string} uiExtensionInfoId the ID of the desired extension info (see Stratus UCDE documentation)
     * @param {string} [currentUiInfoId] optional ID of the current UIInfo
     * @param {string} language the user language in ISO 639-1 format
     * @param {string} [country] the user country in ISO 3166-1 alpha-2 format
     * @param {string} [deviceId] the ID of the device from which this request is being made
     * @param {string} [callType] the type of the onboarding to get info
     * @param {string} [callTypeValue] the value of callType of the onboarding
     * @returns Axios response object
     */
    UiMgtSvcClient.prototype.getUIInfo = function (uiExtensionInfoId, currentUiInfoId, language, country, deviceId, callType, callTypeValue) {
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                return [2 /*return*/, this.jarvisWebHttpInstance.post({
                        url: this.uiInfoEndpoint,
                        params: this._getParams(language, country, deviceId, null),
                        data: this._getUIInfoData(uiExtensionInfoId, currentUiInfoId, callType, callTypeValue),
                        validationSchema: UiMgtSvcClient.uiInfoSchema(),
                    })];
            });
        });
    };
    UiMgtSvcClient.prototype._getUIInfoData = function (uiExtensionInfoId, currentUiInfoId, callType, callTypeValue) {
        var data = {
            uiExtensionInfoId: uiExtensionInfoId,
        };
        if (callType) {
            data.callType = callType;
            data.callValue = callTypeValue;
        }
        if (currentUiInfoId) {
            data.currentUiInfoId = currentUiInfoId;
        }
        return data;
    };
    /**
     * Returns the expected schema for Consents response object.
     *
     * @returns schema in object format
     */
    UiMgtSvcClient.consentsSchema = function () {
        return {
            $schema: 'http://json-schema.org/schema#',
            type: 'object',
            required: ['consents'],
            additionalProperties: true,
            properties: {
                consents: {
                    type: 'array',
                    items: {
                        type: 'object',
                        required: ['state', 'verbiageType', 'verbiageValue'],
                        additionalProperties: true,
                        properties: {
                            state: {
                                type: 'string',
                                enum: ['NoSelection', 'OptedIn', 'OptedOut', 'ReOptInRequired'],
                            },
                            verbiageType: {
                                type: 'string',
                                enum: ['Url', 'Text', 'HTML'],
                            },
                            verbiageValue: {
                                type: 'string',
                            },
                        },
                    },
                },
            },
        };
    };
    /**
     * Retrieves a list of consents available to the authenticated user.
     *
     * @param {string} language the user language in ISO 639-1 format
     * @param {string} [country] the user country in ISO 3166-1 alpha-2 format
     * @param {string} [deviceId] the ID of the device from which this request is being made
     * @param {string} [purposeId] the purposeId of the consent being requested
     *
     * @returns Axios response object
     */
    UiMgtSvcClient.prototype.getConsents = function (language, country, deviceId, purposeId) {
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                return [2 /*return*/, this.jarvisWebHttpInstance.get({
                        url: this.consentsEndpoint,
                        params: this._getParams(language, country, deviceId, purposeId),
                        data: {},
                        validationSchema: UiMgtSvcClient.consentsSchema(),
                    })];
            });
        });
    };
    /**
     * Updates the state of consents available to the authenticated user.
     *
     * @param {object} consentsData updated consents object
     * @returns Axios response object
     */
    UiMgtSvcClient.prototype.updateConsents = function (consentsData) {
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                return [2 /*return*/, this.jarvisWebHttpInstance.patch({
                        url: this.consentsEndpoint,
                        params: this._getParams(),
                        data: this._getUpdateConsentsData(consentsData),
                        validationSchema: UiMgtSvcClient.consentsSchema(),
                    })];
            });
        });
    };
    UiMgtSvcClient.prototype._getUpdateConsentsData = function (consentsData) {
        var data = {
            consentStateChangeRequestMap: {},
        };
        consentsData.consents.forEach(function (element) {
            data.consentStateChangeRequestMap[element.resourceId] = {
                state: element.state,
            };
        });
        return data;
    };
    UiMgtSvcClient.prototype._getParams = function (language, country, deviceId, purposeId) {
        var params = {};
        if (language) {
            params.language = language;
        }
        if (country) {
            params.country = country;
        }
        if (deviceId) {
            params.deviceId = deviceId;
        }
        if (purposeId) {
            params.purposeId = purposeId;
        }
        return params;
    };
    /**
     * Returns the expected schema for Get Countries Info response object.
     *
     * @returns schema in object format
     */
    UiMgtSvcClient.getCountriesInfoSchema = function () {
        return {
            $schema: 'http://json-schema.org/schema#',
            type: 'object',
            required: ['localeInfo'],
            additionalProperties: true,
            properties: {
                supportedCountrySet: {
                    type: 'array',
                },
                supportedLocaleSet: {
                    type: 'array',
                },
            },
        };
    };
    /**
     * Get the country set and language set to the authenticated user.
     *
     * @returns Axios response object
     */
    UiMgtSvcClient.prototype.GetCountriesInfo = function () {
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                return [2 /*return*/, this.jarvisWebHttpInstance.get({
                        url: this.countriesInfoEndpoint,
                        validationSchema: UiMgtSvcClient.getCountriesInfoSchema(),
                    })];
            });
        });
    };
    return UiMgtSvcClient;
}(StratusClient));
export { UiMgtSvcClient };
//# sourceMappingURL=ui-mgt-svc.js.map